import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  can,
  JOB_STAGE_COLORS,
  JOB_STAGE_OUTCOMES,
  type JobStageColor,
  type JobStageOutcome,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { createIntentKey, idem } from '../../lib/idempotency';
import { useSession } from '../session';
import { SectionCard } from '../ui/Detail';
import { Notice, StatusBadge } from '../ui/Badge';
import { Button, iconButton } from '../ui/Button';
import { InlineRename } from '../ui/InlineRename';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { Field, field } from '../create/Field';
import { orderAfterMove } from '../servicetypes/ServiceTypesSettingsCard';
import {
  stageTone,
  type JobPipeline,
  type JobPipelineStage,
} from '../jobs/JobPipelineBoard';
import { IconChevronDown, IconChevronUp, IconJobs, IconPlus } from '../icons';
import { useOnline } from '../ui/useConnection';

/** Tenant configuration; enabling the workflow stays with the reconciled migration rollout. */
export function JobPipelineSettingsCard({ id }: { id: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const role = useSession().role;
  const online = useOnline();
  const mayManage = can(role, 'jobPipeline', 'manage');
  const mayRead =
    (role === 'owner' || role === 'office') && can(role, 'jobPipeline', 'read');
  const q = useQuery({
    ...trpc.jobPipeline.list.queryOptions(),
    enabled: mayRead,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const state = shownState(q, q.data);
  const [name, setName] = useState('');
  const createKey = useRef(createIntentKey());
  const create = useMutation(
    trpc.jobPipeline.create.mutationOptions({
      onSuccess: () => {
        createKey.current.clear();
        setName('');
        void qc.invalidateQueries({ queryKey: trpc.jobPipeline.pathKey() });
      },
    }),
  );
  const locked = !online || q.isFetching || q.isPaused || q.isError || create.isPending;
  return (
    <SectionCard id={id} title="Job pipelines">
      <p className="mb-4 text-sm text-muted-foreground">
        Create named pipelines and define how deals progress. Stages describe the Job;
        confirmation, scheduling, inventory, and payment keep their own states.
      </p>
      <StaleNotice
        show={refreshFailed(q, q.data) || q.isPaused}
        onRetry={() => void q.refetch()}
      />
      {!mayRead ? (
        <Notice tone="warn">You do not have access to job pipelines.</Notice>
      ) : state !== 'ready' ? (
        <UnresolvedState
          state={state}
          icon={IconJobs}
          what="your job pipelines"
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      ) : q.data ? (
        <div className="space-y-5">
          <p className="text-xs text-muted-foreground">
            {q.data.enabled
              ? 'The Jobs workspace is active.'
              : 'Pipelines can be prepared now. The Jobs workspace becomes active after the migration is reconciled.'}
          </p>
          {q.data.pipelines.length === 0 ? (
            <p className="text-sm text-muted-foreground">No pipelines configured.</p>
          ) : null}
          {q.data.pipelines.map((pipeline) => (
            <PipelineEditor
              key={pipeline.id}
              pipeline={pipeline}
              isDefault={pipeline.id === q.data.defaultPipelineId}
              mayManage={mayManage}
              locked={locked}
            />
          ))}
          {mayManage ? (
            <form
              className="space-y-2 border-t border-border pt-4"
              onSubmit={(event) => {
                event.preventDefault();
                if (!locked && name.trim())
                  create.mutate({
                    label: name.trim(),
                    ...createKey.current.forIntent(['pipeline', name.trim()]),
                  });
              }}
            >
              <div className="flex flex-wrap items-end gap-2">
                <Field label="New pipeline">
                  <input
                    aria-label="New pipeline name"
                    className={field}
                    value={name}
                    maxLength={80}
                    readOnly={create.isPending}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="e.g. Installation"
                  />
                </Field>
                <Button type="submit" aria-disabled={locked || !name.trim()}>
                  <IconPlus size={14} />
                  {create.isPending ? 'Adding…' : 'Add pipeline'}
                </Button>
              </div>
              {create.error ? (
                <Notice tone="danger" alert>
                  {create.error.message}
                </Notice>
              ) : null}
            </form>
          ) : null}
        </div>
      ) : null}
    </SectionCard>
  );
}

function PipelineEditor({
  pipeline,
  isDefault,
  mayManage,
  locked,
}: {
  pipeline: JobPipeline;
  isDefault: boolean;
  mayManage: boolean;
  locked: boolean;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  const createStageKey = useRef(createIntentKey());
  const [outcome, setOutcome] = useState<JobStageOutcome>('open');
  const [color, setColor] = useState<JobStageColor>('slate');
  const [error, setError] = useState<string | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: trpc.jobPipeline.pathKey() });
    void qc.invalidateQueries({ queryKey: trpc.commercialJob.pathKey() });
  };
  const done = () => {
    setError(null);
    refresh();
  };
  const failed = (e: { message: string }) => {
    setError(e.message);
    refresh();
  };
  const update = useMutation(
    trpc.jobPipeline.update.mutationOptions({ onSuccess: done, onError: failed }),
  );
  const updateStage = useMutation(
    trpc.jobPipeline.updateStage.mutationOptions({ onSuccess: done, onError: failed }),
  );
  const retire = useMutation(
    trpc.jobPipeline.retireStage.mutationOptions({ onSuccess: done, onError: failed }),
  );
  const createStage = useMutation(
    trpc.jobPipeline.createStage.mutationOptions({
      onSuccess: () => {
        createStageKey.current.clear();
        setLabel('');
        done();
      },
      onError: failed,
    }),
  );
  const reorder = useMutation(
    trpc.jobPipeline.reorderStages.mutationOptions({
      // Return the authoritative settings before the arrows unlock, so a second move cannot
      // compute its order from the pre-save list (the service-type settings lesson).
      onSuccess: (settings) => {
        setError(null);
        qc.setQueryData(trpc.jobPipeline.list.queryKey(), settings);
        void qc.invalidateQueries({ queryKey: trpc.jobPipeline.board.pathKey() });
      },
      onError: failed,
    }),
  );
  const busy =
    locked ||
    update.isPending ||
    updateStage.isPending ||
    retire.isPending ||
    createStage.isPending ||
    reorder.isPending;
  const stages = pipeline.stages.filter((stage) => stage.active);
  const move = (id: string, by: -1 | 1) => {
    if (busy) return;
    const ids = orderAfterMove(
      stages.map((stage) => stage.id),
      id,
      by,
    );
    if (ids)
      reorder.mutate({
        pipelineId: pipeline.id,
        ids,
        expectedVersion: pipeline.version,
        ...idem(),
      });
  };
  return (
    <section
      className="space-y-3 rounded-[var(--radius)] border border-border p-3"
      aria-label={`${pipeline.label} settings`}
    >
      <header className="flex flex-wrap items-center gap-2">
        <h3 className="mr-auto text-sm font-semibold">{pipeline.label}</h3>
        {isDefault ? <StatusBadge tone="brand">Default</StatusBadge> : null}
        {mayManage && !isDefault && pipeline.active ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() =>
              update.mutate({
                id: pipeline.id,
                makeDefault: true,
                expectedVersion: pipeline.version,
                ...idem(),
              })
            }
          >
            Use as default
          </Button>
        ) : null}
        {!pipeline.active ? <StatusBadge tone="neutral">Inactive</StatusBadge> : null}
        {mayManage ? (
          <InlineRename
            value={pipeline.label}
            busy={busy}
            onSave={(next) =>
              update.mutate({
                id: pipeline.id,
                label: next,
                expectedVersion: pipeline.version,
                ...idem(),
              })
            }
          />
        ) : null}
        {mayManage && !isDefault ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() =>
              update.mutate({
                id: pipeline.id,
                active: !pipeline.active,
                expectedVersion: pipeline.version,
                ...idem(),
              })
            }
          >
            {pipeline.active ? 'Retire pipeline' : 'Reactivate pipeline'}
          </Button>
        ) : null}
      </header>
      {error ? (
        <Notice tone="danger" alert>
          {error}
        </Notice>
      ) : null}
      <ol className="divide-y divide-border">
        {stages.map((stage, index) => (
          <li key={stage.id} className="space-y-2 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-auto text-sm">
                <StatusBadge tone={stageTone(stage.color)}>{stage.label}</StatusBadge>
              </span>
              <span className="text-xs capitalize text-muted-foreground">
                {stage.outcome}
              </span>
              {mayManage ? (
                <>
                  <button
                    type="button"
                    className={iconButton}
                    aria-label={`Move ${stage.label} up`}
                    aria-disabled={busy || index === 0}
                    onClick={() => move(stage.id, -1)}
                  >
                    <IconChevronUp size={16} />
                  </button>
                  <button
                    type="button"
                    className={iconButton}
                    aria-label={`Move ${stage.label} down`}
                    aria-disabled={busy || index === stages.length - 1}
                    onClick={() => move(stage.id, 1)}
                  >
                    <IconChevronDown size={16} />
                  </button>
                  <InlineRename
                    value={stage.label}
                    busy={busy}
                    onSave={(next) =>
                      updateStage.mutate({
                        id: stage.id,
                        label: next,
                        expectedVersion: stage.version,
                        ...idem(),
                      })
                    }
                  />
                  <label className="text-xs text-muted-foreground">
                    Color
                    <select
                      className={`${field} mt-1`}
                      aria-label={`Color for ${stage.label}`}
                      value={stage.color}
                      disabled={busy}
                      onChange={(event) =>
                        updateStage.mutate({
                          id: stage.id,
                          color: event.target.value as JobStageColor,
                          expectedVersion: stage.version,
                          ...idem(),
                        })
                      }
                    >
                      {JOB_STAGE_COLORS.map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-xs text-muted-foreground">
                    Outcome
                    <select
                      className={`${field} mt-1`}
                      aria-label={`Outcome for ${stage.label}`}
                      value={stage.outcome}
                      disabled={busy}
                      onChange={(event) =>
                        updateStage.mutate({
                          id: stage.id,
                          outcome: event.target.value as JobStageOutcome,
                          expectedVersion: stage.version,
                          ...idem(),
                        })
                      }
                    >
                      {JOB_STAGE_OUTCOMES.map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              ) : null}
            </div>
            {mayManage && stages.length > 1 ? (
              <RetireStage
                stage={stage}
                stages={stages}
                busy={busy}
                onRetire={(replacementStageId) =>
                  retire.mutate({
                    id: stage.id,
                    replacementStageId,
                    expectedVersion: stage.version,
                    ...idem(),
                  })
                }
              />
            ) : null}
          </li>
        ))}
      </ol>
      {mayManage && pipeline.active ? (
        <form
          className="space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (!busy && label.trim())
              createStage.mutate({
                pipelineId: pipeline.id,
                label: label.trim(),
                outcome,
                color,
                ...createStageKey.current.forIntent([
                  pipeline.id,
                  label.trim(),
                  outcome,
                  color,
                ]),
              });
          }}
        >
          <div className="flex flex-wrap items-end gap-2">
            <Field label="New stage">
              <input
                className={field}
                aria-label={`New stage in ${pipeline.label}`}
                value={label}
                maxLength={80}
                readOnly={busy}
                onChange={(event) => setLabel(event.target.value)}
              />
            </Field>
            <Field label="Outcome">
              <select
                className={field}
                value={outcome}
                disabled={busy}
                onChange={(event) => setOutcome(event.target.value as JobStageOutcome)}
              >
                {JOB_STAGE_OUTCOMES.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Color">
              <select
                className={field}
                value={color}
                disabled={busy}
                onChange={(event) => setColor(event.target.value as JobStageColor)}
              >
                {JOB_STAGE_COLORS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </Field>
            <Button type="submit" aria-disabled={busy || !label.trim()}>
              Add stage
            </Button>
          </div>
        </form>
      ) : null}
      <p className="text-xs text-muted-foreground">
        Retiring a stage requires a replacement for its Jobs. Won and lost are commercial
        outcomes; they do not create work orders or change stock.
      </p>
    </section>
  );
}

function RetireStage({
  stage,
  stages,
  busy,
  onRetire,
}: {
  stage: JobPipelineStage;
  stages: JobPipelineStage[];
  busy: boolean;
  onRetire: (replacementStageId: string) => void;
}) {
  const [replacement, setReplacement] = useState('');
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        className={`${field} max-w-xs`}
        aria-label={`Replacement for ${stage.label}`}
        value={replacement}
        disabled={busy}
        onChange={(event) => setReplacement(event.target.value)}
      >
        <option value="">Choose replacement before retiring…</option>
        {stages
          .filter((next) => next.id !== stage.id)
          .map((next) => (
            <option key={next.id} value={next.id}>
              {next.label}
            </option>
          ))}
      </select>
      <Button
        variant="ghost"
        size="sm"
        disabled={busy || !replacement}
        onClick={() => onRetire(replacement)}
      >
        Retire stage
      </Button>
    </div>
  );
}
